import React, { useState } from "react";
import { Modal, ModalHeader } from "reactstrap";
import DisplayWindow from "./DisplayWindow";
import KeysWindow from "./KeysWindow";
import "./Calculator.css";

const Calculator = ({ isOpen, toggle }) => {
  const [expression, setExpression] = useState("");
  const [displayEXP, setDisplayEXP] = useState("");
  const [result, setResult] = useState("0");
  const [justCalculated, setJustCalculated] = useState(false);

  const sinD = (deg) => Math.sin((deg * Math.PI) / 180);
  const cosD = (deg) => Math.cos((deg * Math.PI) / 180);
  const tanD = (deg) => {
    if (deg % 180 === 90) {
      return Infinity;
    }
    return Math.tan((deg * Math.PI) / 180);
  };

  const asinD = (value) => (Math.asin(value) * 180) / Math.PI;
  const acosD = (value) => (Math.acos(value) * 180) / Math.PI;
  const atanD = (value) => (Math.atan(value) * 180) / Math.PI;
  const cubeRoot = (value) => Math.pow(value, 1 / 3);
  const quadRoot = (value) => Math.pow(value, 1 / 4);

  const permutation = (n, r) => factorial(n) / factorial(n - r);
  const combination = (n, r) => factorial(n) / (factorial(r) * factorial(n - r));

  const sciFunc = {
    sin: "sinD(",
    cos: "cosD(",
    tan: "tanD(",
    "sin⁻¹": "asinD(",
    "cos⁻¹": "acosD(",
    "tan⁻¹": "atanD(",
    ln: "Math.log(",
    log: "Math.log10(",
    π: "Math.PI",
    e: "Math.E",
    "^": "**",
    "√": "Math.sqrt(",
    "³√": "cubeRoot(",
    "⁴√": "quadRoot(",
  };

  function calcResult() {
    if (expression.length !== 0) {
      try {
        let expr = expression;

        // Check for incomplete parentheses
        const openParens = (expr.match(/\(/g) || []).length;
        const closeParens = (expr.match(/\)/g) || []).length;
        if (openParens !== closeParens) {
          setResult("Incomplete Expression");
          return;
        }

        // Handle nPr and nCr
        expr = expr.replace(/(\d+(?:\.\d+)?)nPr(\d+(?:\.\d+)?)/g, (match, n, r) => {
          return permutation(parseFloat(n), parseFloat(r));
        });

        expr = expr.replace(/(\d+(?:\.\d+)?)nCr(\d+(?:\.\d+)?)/g, (match, n, r) => {
          return combination(parseFloat(n), parseFloat(r));
        });

        let compute = eval(expr);
        if (!isFinite(compute)) {
          setResult("undefined");
          return;
        }

        compute = parseFloat(compute.toFixed(4));
        setResult(compute);
        setJustCalculated(true);
      } catch (error) {
        setResult("Invalid Expression");
      }
    } else {
      setResult("0");
    }
  }

  function handleButton(value) {
    // Reset result if it shows an error and user is typing
    if (typeof result === "string" && (result.includes("Error") || result.includes("Incomplete") || result.includes("Invalid"))) {
      if (value !== "=" && value !== "AC") {
        setResult("0");
      }
    }

    // Handle post calculation behavior simply
    if (justCalculated && value !== "=") {
      setJustCalculated(false);

      // For numbers, clear and start fresh
      if (/^\d$/.test(value) || value === ".") {
        setExpression(value);
        setDisplayEXP(value);
        setResult("0");
        return;
      }

      // For operators, continue from result
      if (["+", "-", "*", "/", "^"].includes(value)) {
        setExpression(String(result) + value);
        setDisplayEXP(String(result) + value);
        return;
      }

      // For other buttons (AC, DEL, functions), clear and handle normally
      if (value !== "AC") {
        setExpression("");
        setDisplayEXP("");
        setResult("0");
      }
    }

    if (value === "AC") {
      setExpression("");
      setDisplayEXP("");
      setResult("0");
      setJustCalculated(false);
    } else if (value === "DEL") {
      setDisplayEXP(displayEXP.slice(0, -1));
      setExpression(expression.slice(0, -1));
      // Reset result if expression becomes empty
      if (expression.length <= 1) {
        setResult("0");
      }
    } else if (sciFunc.hasOwnProperty(value)) {
      const functionsWithArgs = ["sin", "cos", "tan", "sin⁻¹", "cos⁻¹", "tan⁻¹", "ln", "log", "√", "³√", "⁴√"];
      if (functionsWithArgs.includes(value)) {
        setDisplayEXP(displayEXP + value + "(");
        setExpression(expression + sciFunc[value]);
      } else {
        setDisplayEXP(displayEXP + value);
        setExpression(expression + sciFunc[value]);
      }
    } else if (value === "nPr" || value === "nCr") {
      setDisplayEXP(displayEXP + value);
      setExpression(expression + value);
    } else if (value === "!") {
      const lastNum = extractLastNum(expression);
      if (lastNum != null) {
        const num = parseFloat(lastNum);
        setDisplayEXP(displayEXP + value);
        setExpression(expression.replace(lastNum, factorial(num)));
      }
    } else if (value === "=") {
      calcResult();
    } else {
      setExpression(expression + value);
      setDisplayEXP(displayEXP + value);
    }
  }

  function factorial(n) {
    let result = 1;
    for (let i = 1; i <= n; i++) result *= i;
    return result;
  }

  function extractLastNum(exp) {
    const numbers = exp.match(/\d+(\.\d+)?/g);
    return numbers ? numbers[numbers.length - 1] : null;
  }

  return (
    <div className="calcbody">
      <Modal
        size="lg"
        isOpen={isOpen}
        toggle={toggle}
        backdrop={false}
        className="
       modal-top-right"
      >
        <ModalHeader toggle={toggle}>Scientific Calculator</ModalHeader>
        <div className="calculator">
          <DisplayWindow expression={displayEXP} result={result} />
          <KeysWindow handleButton={handleButton} />
        </div>
      </Modal>
    </div>
  );
};

export default Calculator;
